import {inject, Injectable} from '@angular/core';
import {AuthStore} from '../state/auth.store';
import {AuthQuery} from '../state/auth.query';
import {AuthApiService} from './auth-api.service';
import {Router} from '@angular/router';
import {ErrorResponse, LoginRequest, RegisterRequest, User, ValidationError} from '../../models/auth.models';
import {catchError, Observable, of, switchMap, tap, throwError} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authStore = inject(AuthStore);
  private authQuery = inject(AuthQuery);
  private authApi = inject(AuthApiService);
  private router = inject(Router);

  private readonly TOKEN_KEY = 'auth_tokens';
  private readonly USER_KEY = 'auth_user';

  constructor() {
    this.loadStoredAuth();
  }

  login(credentials: LoginRequest): Observable<void> {
    this.authStore.setAuthLoading(true);
    this.authStore.clearErrors();

    return this.authApi.login(credentials).pipe(
      tap(response => {
        const user: User = {
          email: credentials.email,
          firstName: '',
          lastName: '',
          phoneNumber: ''
        };

        this.handleAuthSuccess(user, {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          token_type: response.token_type
        });
      }),
      switchMap(() => of(void 0)),
      catchError(error => {
        this.handleError(error);
        return throwError(() => error);
      })
    );
  }

  register(data: RegisterRequest): Observable<void> {
    this.authStore.setAuthLoading(true);
    this.authStore.clearErrors();

    return this.authApi.register(data).pipe(
      tap(() => {
        this.authStore.setAuthLoading(false);
        // After successful registration, auto-login
        this.login({
          email: data.email,
          password: data.password
        }).subscribe();
      }),
      catchError(error => {
        this.handleError(error);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    this.clearAuth();
    this.router.navigate(['/accueil']);
  }

  refreshToken(): Observable<boolean> {
    const refreshToken = this.authQuery.getRefreshToken();

    if (!refreshToken) {
      return of(false);
    }

    return this.authApi.refreshToken({ refreshToken }).pipe(
      tap(response => {
        const updatedTokens = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          token_type: response.token_type
        };
        this.authStore.updateTokens(updatedTokens);
        this.storeTokens(updatedTokens);
      }),
      switchMap(() => of(true)),
      catchError(error => {
        this.clearAuth();
        return of(false);
      })
    );
  }

  private handleAuthSuccess(user: User, tokens: any): void {
    this.authStore.login(user, tokens);
    this.storeAuth(user, tokens);
  }

  private storeAuth(user: User, tokens: any): void {
    localStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokens));
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private storeTokens(tokens: any): void {
    localStorage.setItem(this.TOKEN_KEY, JSON.stringify(tokens));
  }

  private loadStoredAuth(): void {
    const tokens = localStorage.getItem(this.TOKEN_KEY);
    const user = localStorage.getItem(this.USER_KEY);

    if (tokens && user) {
      try {
        const parsedTokens = JSON.parse(tokens);
        const parsedUser = JSON.parse(user);
        this.authStore.login(parsedUser, parsedTokens);
      } catch (error) {
        this.clearAuth();
      }
    }
  }

  private clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.authStore.logout();
  }

  isAuthenticated(): boolean {
    return this.authQuery.isLoggedIn();
  }

  private handleError(error: any): void {
    const errorResponse: ErrorResponse = error.error;

    if (errorResponse?.validationErrors && errorResponse.validationErrors.length > 0) {
      // Handle validation errors
      const errorMessage = this.formatValidationErrors(errorResponse.validationErrors);
      this.authStore.setAuthError(errorMessage, errorResponse.validationErrors);
    } else if (errorResponse?.message) {
      // Handle general error message
      this.authStore.setAuthError(errorResponse.message, []);
    } else {
      // Fallback error message
      this.authStore.setAuthError('Une erreur est survenue', []);
    }
  }

  private formatValidationErrors(errors: ValidationError[]): string {
    if (errors.length === 1) {
      return errors[0].message;
    }
    return `${errors.length} erreurs de validation`;
  }

}
